import React from 'react';
import { Select, TreeSelect } from 'antd';
import { CoreBrandsTreeSelector } from '../../../styles/components/CoreBrandsTable.styled';
import { CoreBrandData } from '../types';
import { createDropdownVisibleChangeHandler } from '../../../hooks/SettingsPage/dropdownHandlers';
import BrandSelectorPopup from './BrandSelector/BrandSelectorPopup';
import { ArrowDownIcon, CloseIcon, AddIcon } from './coreBrandsIcons';

// CoreBrandsTreeSelector is an untyped styled(TreeSelect) that also takes a
// styling-only `isEmpty` prop; re-type it so that extra prop type-checks
// alongside antd's TreeSelect props.
const CoreBrandsTreeSelectorTyped = CoreBrandsTreeSelector as React.ComponentType<
  React.ComponentProps<typeof TreeSelect> & { isEmpty?: boolean }
>;

/**
 * Parameters for Core Brands table columns configuration
 */
interface CoreBrandsColumnsParams {
  // Editing states
  isEditingCoreBrands: boolean;
  editingCoreBrandsRows: { [key: string]: boolean };
  editingCoreBrandsData: { [key: string]: Partial<CoreBrandData> };
  displayCoreBrandsData: CoreBrandData[];

  // Select states
  openSelectKeys: Set<string>;
  selectActivatedKeys: Set<string>;
  setSelectActivatedKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  setOpenSelectKeys: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Brand data
  brandOptions: any[];
  brandDataLoading: boolean;
  companiesWithBrandsLoading: boolean;

  // Auth
  authIdClient: string;

  // Handler functions
  handleCoreBrandInputChange: (key: string, field: keyof CoreBrandData, value: any) => void;
  handleCoreBrandClearAndCompact: (key: string, clearedIndex: number) => void;
  updateTreeSelectState: (fieldKey: string, updates: any) => void;
  getTreeSelectState: (fieldKey: string) => any;
  fetchBrandData: (countryCode: string, authIdClient: string) => void;
  getCountryCodeFromMarket: (marketName: string) => string;
  getSelectedBrandNames: (selectedValues: string[], brandOptions: any[]) => string[];
  generateCoreBrandData: (customName: string, selectedBrands: string[]) => any;
  getFilteredBrandOptions: (searchValue: string) => any[];
  findBrandByName: (brandName: string, brandOptions: any[], subbrandName?: string | null) => { brand: any; subbrand: any; value: string } | null;
  mergeCompleteSubbrandsToParent: (selectedBrands: string[], brandOptions: any[]) => string[];
  fetchCompetitorsData: (countryCode: string, authIdClient: string) => Promise<void>;
  getCompetitorsTreeData: (selectedCompany?: string) => any[];
}

/**
 * Generate market column configuration
 */
export const getMarketColumn = (): any => ({
  title: (
    <div className="flex items-center gap-1 justify-left px-3">Market</div>
  ),
  dataIndex: "market",
  key: "market",
  width: 80,
  sorter: (a: CoreBrandData, b: CoreBrandData) =>
    a.market.localeCompare(b.market),
  render: (text: string) => (
    <div className="flex items-center px-3">
      <span className="text-[0.75rem] text-left">{text}</span>
    </div>
  ),
});

/**
 * Generate core brand columns configuration (8 columns)
 */
export const getCoreBrandColumns = (params: CoreBrandsColumnsParams): any[] => {
  const {
    isEditingCoreBrands,
    editingCoreBrandsRows,
    editingCoreBrandsData,
    openSelectKeys,
    selectActivatedKeys,
    setSelectActivatedKeys,
    setOpenSelectKeys,
    brandOptions,
    brandDataLoading,
    authIdClient,
    handleCoreBrandInputChange,
    handleCoreBrandClearAndCompact,
    updateTreeSelectState,
    getTreeSelectState,
    fetchBrandData,
    getCountryCodeFromMarket,
    getSelectedBrandNames,
    generateCoreBrandData,
    getFilteredBrandOptions: _getFilteredBrandOptions, // Currently unused, may be needed for future search/filter functionality
    findBrandByName,
    mergeCompleteSubbrandsToParent,
    displayCoreBrandsData,
  } = params;

  return Array.from({ length: 8 }, (_, i) => ({
    title: (
      <div className="flex items-center gap-1 justify-center px-1">
        Core brand {i + 1}
      </div>
    ),
    dataIndex: `coreBrand${i + 1}`,
    key: `coreBrand${i + 1}`,
    width: 110,
    render: (text: string, record: any) => {
      const fieldKey = `${record.key}_coreBrand${i + 1}`;
      const fieldName = `coreBrand${i + 1}` as keyof CoreBrandData;

      // 🎯 Unsaved edits take precedence over the row's stored value.
      // Use explicit undefined check so empty string ("") is treated as cleared, not skipped
      const editingValue = editingCoreBrandsData[record.key]?.[fieldName];
      const currentValue = editingValue !== undefined ? editingValue : (record[fieldName] || text || "");

      const isEmpty = !currentValue || currentValue === "-" || (typeof currentValue === 'string' && currentValue.trim() === "");

      const state = getTreeSelectState(fieldKey);
      const hasTempValue = !!(state.tempCustomName || state.customName);
      const hasCurrentValue = !isEmpty && currentValue !== "-";
      const hasValue = hasTempValue || hasCurrentValue;
      const isSelectOpen = openSelectKeys.has(fieldKey);
      const isSelectActivated = selectActivatedKeys.has(fieldKey);
      const shouldShowSelect = hasValue || isSelectActivated;
      const dropdownHandler = createDropdownVisibleChangeHandler({
        record,
        i,
        displayCoreBrandsData,
        editingCoreBrandsData,
        updateTreeSelectState,
        getTreeSelectState,
        fetchBrandData,
        getCountryCodeFromMarket,
        authIdClient,
        findBrandByName,
        mergeCompleteSubbrandsToParent,
        brandOptions,
      });

      return (
        <div className="flex items-center">
          {(isEditingCoreBrands || editingCoreBrandsRows[record.key]) ? (
            // 🎯 An empty or "-" slot renders a "+ Add" button in place of the select.
            (!shouldShowSelect && !isSelectOpen) ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // 🎯 Activating the slot keeps it in select form and opens its dropdown.
                  setSelectActivatedKeys(prev => {
                    const next = new Set(prev);
                    next.add(fieldKey);
                    return next;
                  });
                  setOpenSelectKeys(prev => {
                    const next = new Set(prev);
                    next.add(fieldKey);
                    return next;
                  });
                  dropdownHandler(true);
                }}
                className="flex items-center justify-center gap-1 px-2 py-1 text-black/35 hover:bg-gray-100 rounded transition-colors text-xs cursor-pointer"
                style={{
                  width: '100%',
                  borderRadius: '9999px',
                  height: '28px'
                }}
              >
                <AddIcon style={{ fontSize: '12px' }} />
                <span>Add</span>
              </button>
            ) : (
              <Select
                placeholder="Select brand"
                style={{ width: '100%', borderRadius: '9999px', height: '28px', fontSize: '0.75rem' }}
                data-field-key={fieldKey}
                open={isSelectOpen ? true : undefined}
                value={(() => {
                  // TreeSelect state takes priority (set when dropdown is opened)
                  if (state.tempCustomName || state.customName) {
                    return state.tempCustomName || state.customName;
                  }
                  // Fall back to display name from current value so the X button
                  // appears for existing brands before the dropdown is opened
                  if (!isEmpty && currentValue && currentValue !== "-") {
                    try {
                      const parsed = JSON.parse(currentValue);
                      return parsed.displayName || null;
                    } catch {
                      return currentValue;
                    }
                  }
                  return undefined;
                })()}
                onChange={() => {
                  // 🎯 Selection is applied by CustomBrandTree; the Select only
                  // drives the dropdown.
                }}
                onClear={() => {
                  // Compact remaining brands left to fill the gap
                  handleCoreBrandClearAndCompact(record.key, i);

                  // Clear TreeSelect states for all 8 slots (they will re-init on next open)
                  for (let j = 0; j < 8; j++) {
                    updateTreeSelectState(`${record.key}_coreBrand${j + 1}`, {
                      customName: "",
                      tempCustomName: "",
                      selectedBrands: [],
                      tempSelectedBrands: [],
                      searchValue: "",
                      hasError: false
                    });
                  }

                  setOpenSelectKeys(prev => {
                    const next = new Set(prev);
                    next.delete(fieldKey);
                    return next;
                  });

                  setSelectActivatedKeys(prev => {
                    const next = new Set(prev);
                    next.delete(fieldKey);
                    return next;
                  });
                }}
                allowClear
                loading={brandDataLoading}
                suffixIcon={<ArrowDownIcon />}
                showSearch={false}
                styles={{
                  popup: {
                    root: {
                      padding: 0,
                      border: 'none',
                      boxShadow: 'none',
                      minWidth: "400px",
                      borderRadius: "1.5rem",
                    },
                  },
                }}
                onOpenChange={(open) => {
                  setOpenSelectKeys(prev => {
                    const next = new Set(prev);
                    if (open) {
                      next.add(fieldKey);
                    } else {
                      next.delete(fieldKey);
                    }
                    return next;
                  });

                  dropdownHandler(open);
                }}
                popupRender={() => {
                  const state = getTreeSelectState(fieldKey);
                  const selectedBrandNames = getSelectedBrandNames(state.tempSelectedBrands, brandOptions);

                  return (
                    <BrandSelectorPopup
                      state={state}
                      selectedBrandNames={selectedBrandNames}
                      brandOptions={brandOptions}
                      brandDataLoading={brandDataLoading}
                      fieldKey={fieldKey}
                      record={record}
                      i={i}
                      updateTreeSelectState={updateTreeSelectState}
                      generateCoreBrandData={generateCoreBrandData}
                      handleCoreBrandInputChange={handleCoreBrandInputChange}
                      mergeCompleteSubbrandsToParent={(selectedBrands: string[]) => mergeCompleteSubbrandsToParent(selectedBrands, brandOptions)}
                      findBrandByName={(brandName: string, subbrandName?: string | null) => findBrandByName(brandName, brandOptions, subbrandName)}
                    />
                  );
                }}
              />
            )
          ) : (
          <div className="mx-3 text-left text-[0.75rem]">
            {(() => {
              // 🎯 A stored brand may be JSON carrying displayName, or plain text.
              if (text && text !== "-") {
                try {
                  const parsedData = JSON.parse(text);
                  return parsedData.displayName || text;
                } catch {
                  return text;
                }
              }
              return text;
            })()}
          </div>
          )}
        </div>
      );
    }
  }));
};

/**
 * Generate competitor column configuration
 */
export const getCompetitorColumn = (params: CoreBrandsColumnsParams): any => {
  const {
    isEditingCoreBrands,
    editingCoreBrandsRows,
    editingCoreBrandsData,
    companiesWithBrandsLoading,
    authIdClient,
    handleCoreBrandInputChange,
    getCountryCodeFromMarket,
    fetchCompetitorsData,
    getCompetitorsTreeData,
  } = params;

  const parseCompetitorValue = (competitorText?: string): string[] => {
    if (!competitorText || competitorText === "-") {
      return [];
    }

    try {
      // JSON is the primary stored format.
      const parsed = JSON.parse(competitorText);

      if (parsed.competitors && Array.isArray(parsed.competitors)) {
        return parsed.competitors.map((c: any) => {
          const company = c.company || c.competitor || '';
          return c.brand ? `${company}|${c.brand}` : company;
        });
      }
    } catch {
      // A non-JSON value is a plain string naming a single competitor.
      const trimmedText = competitorText.trim();
      if (trimmedText) {
        return [trimmedText];
      }
    }

    return [];
  };

  const tagRender = (props: any) => {
    const { label, closable, onClose } = props;
    return (
      <div
        className="px-2 py-0.5 mb-0.5 bg-slate-50 rounded-[999px] outline-[0.50px] outline-offset-[-0.50px] outline-black/5 inline-flex justify-center items-center"
        style={{ marginRight: '4px' }}
      >
        <div className="justify-start text-[#0E49B5] text-xs font-normal leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
          {label}
        </div>
        {closable && (
          <div
            className="size-4 relative overflow-hidden cursor-pointer flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <CloseIcon style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.4)' }} />
          </div>
        )}
      </div>
    );
  };

  return {
    title: (
      <div className="flex items-center gap-1 justify-center px-3">
        Competitor
      </div>
    ),
    dataIndex: "competitor",
    key: "competitor",
    width: 100,
    sorter: (a: CoreBrandData, b: CoreBrandData) =>
      a.competitor.localeCompare(b.competitor),
    render: (text: string, record: CoreBrandData) => {
      // 🎯 The row's market yields the country code the competitors fetch needs.
      const currentMarket = record.market;
      const countryCode = getCountryCodeFromMarket(currentMarket);

      // 🎯 CoreBrandsTable rows carry no company field, so the competitor tree
      // is left unfiltered.
      const selectedCompany = undefined;

      return (
        <div className="flex items-center">
          {(isEditingCoreBrands || editingCoreBrandsRows[record.key]) ? (
            <CoreBrandsTreeSelectorTyped
              multiple
              treeCheckable
              placeholder={
                companiesWithBrandsLoading
                  ? "Loading competitors..."
                  : "Select competitors (companies or brands)..."
              }
              style={{ width: '100%'}}
              isEmpty={(() => {
                const editingValue = editingCoreBrandsData[record.key]?.competitor;
                const dataToUse = editingValue !== undefined ? editingValue : record.competitor;
                const parsedValue = parseCompetitorValue(dataToUse);
                return parsedValue.length === 0;
              })()}
              value={(() => {
                const editingValue = editingCoreBrandsData[record.key]?.competitor;
                // 🔧 Use original record data if editing value is undefined
                const dataToUse = editingValue !== undefined ? editingValue : record.competitor;
                return parseCompetitorValue(dataToUse);
              })()}
              onChange={(values: string[]) => {
                // 🎯 Convert the TreeSelect values into the stored shape.
                const competitorsList = values.map((val) => {
                  if (val.includes("|")) {
                    // Individual brand selected: company|brandName format
                    const [company, brandName] = val.split("|");
                    return { company, brand: brandName };
                  } else {
                    // Whole company selected (all brands included)
                    return { company: val, brand: null };
                  }
                });

                const competitorValue = JSON.stringify({ competitors: competitorsList });
                handleCoreBrandInputChange(record.key, 'competitor', competitorValue);
              }}
              treeData={getCompetitorsTreeData(selectedCompany)}
              allowClear
              maxTagCount={2}
              maxTagTextLength={10}
              showSearch
              loading={companiesWithBrandsLoading}
              treeDefaultExpandAll={false}
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              tagRender={tagRender}
              styles={{
                popup: {
                  root: {
                    minWidth: "280px",
                    borderRadius: "16px",
                  },
                },
              }}
              onOpenChange={(open: boolean) => {
                if (open && countryCode && authIdClient) {
                  // 🎯 Competitors are fetched when the dropdown opens, not up front.
                  fetchCompetitorsData(countryCode, authIdClient);
                }
              }}
              suffixIcon={<ArrowDownIcon />}
            />
          ) : (
            <div className="text-left text-[0.75rem] mx-3">
              {(() => {
                // 🎯 A stored competitor list may be JSON, or plain text.
                if (text && text !== "-") {
                  try {
                    const parsed = JSON.parse(text);
                    if (parsed.competitors && Array.isArray(parsed.competitors)) {
                      return parsed.competitors
                        .map((c: any) => { const co = c.company || c.competitor || ''; return c.brand ? `${co} / ${c.brand}` : co; })
                        .join("; ");
                    }
                  } catch {
                    return text;
                  }
                }
                return text;
              })()}
            </div>
          )}
        </div>
      );
    },
  };
};

/**
 * Main function to generate all Core Brands table columns
 */
export const getCoreBrandsColumns = (params: CoreBrandsColumnsParams): any[] => [
  getMarketColumn(),
  ...getCoreBrandColumns(params),
  getCompetitorColumn(params),
];